import React, { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Navigate, useLocation, useParams } from 'react-router-dom'

import { Grid, Tooltip } from '@mui/material'

import BetaBadge from '../../components/BetaBadge/BetaBadge'
import GRTabNavigation, { GRSubTabNavigation } from '../../components/GRTabNavigation/GRTabNavigation'
import { CollectionPage } from '../../features/collections'
import ImplementationExamples from '../../features/implementation-examples/components/ImplementationExamples/ImplementationExamples'
import { LiveEventExamples } from '../../features/implementation-examples/components/LiveEventExamples/LiveEventExamples'
import usePage from '../../hooks/usePage'
import pageService from '../../services/PageService'
import './ImplementationExamplesPage.scss'

const ImplementationExamplesPage: React.FC = () => {
  const { pathname, search } = useLocation()
  const needsRedirect = pathname === '/implementation-examples'
  const { t } = useTranslation()

  usePage(pageService.getPageWithId('implementation-examples'), 'Visited Feature Implementation Examples')
  const params: { tab?: string; subtab?: string } = useParams()

  const tabs = useMemo(
    () => [
      {
        id: 'feature-examples',
        name: t('implementations:tab_feature_examples'),
      },
      {
        id: 'live-event-examples',
        name: (
          <Grid container alignItems="center">
            <Tooltip title={t('implementations:tab_live_event_examples_beta_tooltip')}>
              <span>
                <BetaBadge sx={{ mr: 1, my: 0 }} color="primary" />
              </span>
            </Tooltip>
            <Trans i18nKey="implementations:tab_live_event_examples" />
          </Grid>
        ),
      },
    ],
    [t]
  )

  const defaultTab = tabs[0]
  const [selectedTab, setSelectedTab] = useState(tabs.find((subpage) => subpage.id === params.tab) || defaultTab)

  const subTabs = useMemo(
    () => [
      {
        id: 'examples',
        name: t('implementations:subtab_implementation_examples'),
      },
      {
        id: 'collections',
        name: t('implementations:subtab_collections'),
      },
    ],
    [t]
  )
  const defaultSubTab = subTabs[0]
  const [selectedSubTab, setSelectedSubTab] = useState(subTabs.find((subTab) => subTab.id === params.subtab) || defaultSubTab)

  return needsRedirect ? (
    <Navigate replace to="/implementation-examples/feature-examples" />
  ) : (
    <div className="ImplementationExamplesPage">
      <GRTabNavigation
        tabs={tabs}
        selectedTab={tabs.findIndex((tab) => tab.id === selectedTab.id)}
        onChange={(tabIndex) => setSelectedTab(tabs[tabIndex])}
        pagePrefix={`/implementation-examples`}
        sticky
        hasSecondaryStickyElement
      />

      {selectedTab.id === 'feature-examples' && (
        <>
          <GRSubTabNavigation
            tabs={subTabs}
            selectedTab={subTabs.find((tab) => tab.id === selectedSubTab.id)?.id}
            onChange={(event, newValue) => setSelectedSubTab(subTabs.find((subTab) => subTab.id === newValue) || defaultSubTab)}
            pagePrefix={`/implementation-examples/feature-examples`}
            query={search}
          />
          {selectedSubTab.id === 'examples' && <ImplementationExamples />}
          {selectedSubTab.id === 'collections' && <CollectionPage />}
        </>
      )}
      {selectedTab.id === 'live-event-examples' && <LiveEventExamples />}
    </div>
  )
}

export default ImplementationExamplesPage
